import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { API } from '../lib/endpoints'
import type {
  Message, MessageWrite, MessageTemplate,
  SMSProviderConfig, WhatsAppProviderConfig, ConnectionStatus,
  Conversation, ConversationDetail, InternalMessage,
  BulkSendPayload, MessagingStats,
} from '../types/api'

// ── Query Keys ───────────────────────────────────────────────────

export const messageKeys = {
  all: ['messages'] as const,
  list: (params?: Record<string, string>) => [...messageKeys.all, 'list', params ?? {}] as const,
  detail: (id: number) => [...messageKeys.all, 'detail', id] as const,
}

export const messageTemplateKeys = {
  all: ['message-templates'] as const,
  list: (params?: Record<string, string>) => [...messageTemplateKeys.all, 'list', params ?? {}] as const,
}

export const providerKeys = {
  all: ['providers'] as const,
  sms: () => [...providerKeys.all, 'sms'] as const,
  whatsapp: () => [...providerKeys.all, 'whatsapp'] as const,
  connection: () => [...providerKeys.all, 'connection'] as const,
}

// ── Messages ─────────────────────────────────────────────────────

export function useMessages(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return useQuery({
    queryKey: messageKeys.list(params),
    queryFn: () => api.get<Message[]>(`${API.messages.list}${qs}`),
  })
}

export function useMessage(id: number) {
  return useQuery({
    queryKey: messageKeys.detail(id),
    queryFn: () => api.get<Message>(API.messages.detail(id)),
    enabled: !!id,
  })
}

export function useCreateMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: MessageWrite) =>
      api.post<Message>(API.messages.list, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageKeys.all })
    },
  })
}

export function useCancelMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Message>(API.messages.cancel(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageKeys.all })
    },
  })
}

export function useSendNowMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Message>(API.messages.sendNow(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageKeys.all })
    },
  })
}

// ── Message Templates ────────────────────────────────────────────

export function useMessageTemplates(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return useQuery({
    queryKey: messageTemplateKeys.list(params),
    queryFn: () => api.get<MessageTemplate[]>(`${API.messageTemplates.list}${qs}`),
  })
}

export function useCreateMessageTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<MessageTemplate>) =>
      api.post<MessageTemplate>(API.messageTemplates.list, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageTemplateKeys.all })
    },
  })
}

export function useUpdateMessageTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<MessageTemplate> }) =>
      api.patch<MessageTemplate>(API.messageTemplates.detail(id), data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageTemplateKeys.all })
    },
  })
}

export function useDeleteMessageTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(API.messageTemplates.detail(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageTemplateKeys.all })
    },
  })
}

// ── Provider Configs ─────────────────────────────────────────────

export function useSMSConfig() {
  return useQuery({
    queryKey: providerKeys.sms(),
    queryFn: () => api.get<SMSProviderConfig>(API.smsProvider),
  })
}

export function useUpdateSMSConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<SMSProviderConfig & { api_key?: string; api_secret?: string }>) =>
      api.put<SMSProviderConfig>(API.smsProvider, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: providerKeys.sms() })
      qc.invalidateQueries({ queryKey: providerKeys.connection() })
    },
  })
}

export function useWhatsAppConfig() {
  return useQuery({
    queryKey: providerKeys.whatsapp(),
    queryFn: () => api.get<WhatsAppProviderConfig>(API.whatsappProvider),
  })
}

export function useUpdateWhatsAppConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<WhatsAppProviderConfig & { api_key?: string }>) =>
      api.put<WhatsAppProviderConfig>(API.whatsappProvider, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: providerKeys.whatsapp() })
      qc.invalidateQueries({ queryKey: providerKeys.connection() })
    },
  })
}

export function useConnectionStatus() {
  return useQuery({
    queryKey: providerKeys.connection(),
    queryFn: () => api.get<ConnectionStatus>(API.connectionStatus),
    staleTime: 30_000,
  })
}

// ── Internal Chat ───────────────────────────────────────────────

export const conversationKeys = {
  all: ['conversations'] as const,
  list: () => [...conversationKeys.all, 'list'] as const,
  detail: (id: number) => [...conversationKeys.all, 'detail', id] as const,
  unread: () => [...conversationKeys.all, 'unread'] as const,
}

export function useConversations() {
  return useQuery({
    queryKey: conversationKeys.list(),
    queryFn: () => api.get<Conversation[]>(API.conversations.list),
  })
}

export function useConversation(id: number) {
  return useQuery({
    queryKey: conversationKeys.detail(id),
    queryFn: () => api.get<ConversationDetail>(API.conversations.detail(id)),
    enabled: !!id,
    refetchInterval: 15_000,
  })
}

export function useCreateConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { title?: string; user_ids: number[] }) =>
      api.post<Conversation>(API.conversations.list, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })
}

export function useDirectConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<Conversation>(API.conversations.direct, { user_id: userId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })
}

export function useSendInternalMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ conversationId, body }: { conversationId: number; body: string }) =>
      api.post<InternalMessage>(API.conversations.sendMessage(conversationId), { body }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: conversationKeys.detail(variables.conversationId) })
      qc.invalidateQueries({ queryKey: conversationKeys.list() })
    },
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: number) =>
      api.post(API.conversations.markRead(conversationId), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })
}

export function useUnreadCount() {
  return useQuery({
    queryKey: conversationKeys.unread(),
    queryFn: async () => {
      try {
        return await api.get<{ unread_count: number }>(API.messagingUnreadCount)
      } catch {
        // Module disabled or permission denied → return 0
        return { unread_count: 0 }
      }
    },
    staleTime: 15_000,
    // 30s (was 60s): drives the active "new message" toast + sidebar badge, so
    // a colleague's message surfaces within ~half a minute. Cheap for a clinic.
    refetchInterval: 30_000,
  })
}

export function useEditMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      api.patch<InternalMessage>(API.internalMessage(id), { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })
}

export function useDeleteMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(API.internalMessage(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })
}

// ── Bulk Send ───────────────────────────────────────────────────

export function useBulkSend() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkSendPayload) =>
      api.post<{ created: number }>(API.messagingBulkSend, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messageKeys.all })
    },
  })
}

// ── Stats ───────────────────────────────────────────────────────

export function useMessagingStats() {
  return useQuery({
    queryKey: ['messaging-stats'],
    queryFn: () => api.get<MessagingStats>(API.messagingStats),
    staleTime: 30_000,
  })
}

// ── Template Preview ────────────────────────────────────────────

export function useTemplatePreview() {
  return useMutation({
    mutationFn: ({ id, context }: { id: number; context: Record<string, string> }) =>
      api.post<{ rendered_body: string; rendered_body_ar: string }>(API.messageTemplates.preview(id), context),
  })
}

// ── Patient Messages ────────────────────────────────────────────

export function usePatientMessages(patientId: number) {
  return useQuery({
    queryKey: ['patient-messages', patientId],
    queryFn: () => api.get<Message[]>(API.patientMessages(patientId)),
    enabled: !!patientId,
  })
}
